import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  createNuwweDistributions,
  createNuwweProperty,
  T_createProperty_arg,
} from "../api";
import { Button, toast } from "@coninsa/core";
import { queryString, safeAwait } from "@coninsa/utils";

import { T_fields as T_step2_fields } from "./Step2";

type T_props = {
  onSuccess: () => void;
  onBack: () => void;
};

type T_fields = {
  area_m2: string;
  bathrooms: string;
  rooms: string;
  num_parqueaderos: string;
  utility_room: string;
  description: string;
};

function Step3({ onSuccess, onBack }: T_props) {
  const {
    formState: { isSubmitting },
    handleSubmit,
    register,
    watch,
  } = useForm<T_fields>({
    defaultValues: async () => {
      const values = JSON.parse(
        localStorage.getItem("consignation.distribution") ?? "{}"
      );

      return values;
    },
  });

  const onSubmit = async (data) => {
    localStorage.setItem("consignation.distribution", JSON.stringify(data));

    const propertyData = JSON.parse(
      localStorage.getItem("consignation.property") ?? "{}"
    ) as T_step2_fields;

    const propertyBody: T_createProperty_arg = {
      cod_sucursal: propertyData.cod_sucursal,
      cod_tipo_inmueble: propertyData.cod_tipo_inmueble,
      cod_estrato: propertyData.cod_estrato,
      vlr_canon: propertyData.vlr_canon,
      vlr_administracion: propertyData.vlr_administracion,
      cod_barrio: propertyData.cod_barrio.value,
      dir_inmueble: [
        propertyData.dir_inmueble,
        [
          propertyData.tower_number,
          propertyData.apartment_number,
          propertyData.building_co_ownership,
        ].join(" "),
      ].join(", "),
      obs_inmueble: data.description,
    };

    const [propertyError, response] = await safeAwait(
      createNuwweProperty(propertyBody)
    );

    if (propertyError) {
      toast.error({
        title: "Algo está mal.",
        message: "Vuelva a intentarlo más tarde",
      });

      return;
    }

    const code = response?.datos?.cod_inmueble;

    if (!code) {
      toast.error({ message: "El código de propiedad no está definido." });
      return;
    }

    queryString.set("property", code);

    const [error] = await safeAwait(createNuwweDistributions(code, data));

    if (error) {
      toast.error({
        title: "Algo está mal.",
        message: "Vuelva a intentarlo más tarde",
      });
    }

    onSuccess();
  };

  const description = watch("description");

  const characterLimit = 589;
  const [length, setLength] = useState(0);

  useEffect(() => {
    if (description) {
      setLength(description.length);
    }
  }, [description]);

  return (
    <div className="co-card max-w-4xl mx-auto">
      <form className="co-form gap-y-8" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-wrap gap-4">
          <div className="form-control grow">
            <label>Área del inmueble (m2)</label>
            <input
              {...register("area_m2")}
              type="text"
              id="property_area"
              placeholder="Ingresa el área de tu inmueble"
              required
            />
          </div>
        </div>

        <div className="flex justify-between flex-wrap gap-4 flex-col md:flex-row">
          <fieldset className="form-group are-compact-radios">
            <legend>Número de baños</legend>
            <div className="form-items">
              <div className="form-control">
                <input
                  {...register("bathrooms")}
                  type="radio"
                  id="bathrooms_1"
                  value="1"
                  required
                />
                <label htmlFor="bathrooms_1">1</label>
              </div>
              <div className="form-control">
                <input
                  {...register("bathrooms")}
                  type="radio"
                  id="bathrooms_2"
                  value="2"
                  required
                />
                <label htmlFor="bathrooms_2">2</label>
              </div>
              <div className="form-control">
                <input
                  {...register("bathrooms")}
                  type="radio"
                  id="bathrooms_3"
                  value="3"
                  required
                />
                <label htmlFor="bathrooms_3">3+</label>
              </div>
            </div>
          </fieldset>

          <fieldset className="form-group are-compact-radios">
            <legend>Número de alcobas</legend>
            <div className="form-items">
              <div className="form-control">
                <input
                  {...register("rooms")}
                  type="radio"
                  id="rooms_1"
                  value="1"
                  required
                />
                <label htmlFor="rooms_1">1</label>
              </div>
              <div className="form-control">
                <input
                  {...register("rooms")}
                  type="radio"
                  id="rooms_2"
                  value="2"
                  required
                />
                <label htmlFor="rooms_2">2</label>
              </div>
              <div className="form-control">
                <input
                  {...register("rooms")}
                  type="radio"
                  id="rooms_3"
                  value="3"
                  required
                />
                <label htmlFor="rooms_3">3+</label>
              </div>
            </div>
          </fieldset>

          <fieldset className="form-group are-compact-radios">
            <legend>Número de parqueaderos</legend>
            <div className="form-items">
              <div className="form-control">
                <input
                  {...register("num_parqueaderos")}
                  type="radio"
                  id="garages_0"
                  value="0"
                  required
                />
                <label htmlFor="garages_0">0</label>
              </div>
              <div className="form-control">
                <input
                  {...register("num_parqueaderos")}
                  type="radio"
                  id="garages_1"
                  value="1"
                  required
                />
                <label htmlFor="garages_1">1</label>
              </div>
              <div className="form-control">
                <input
                  {...register("num_parqueaderos")}
                  type="radio"
                  id="garages_2"
                  value="2"
                  required
                />
                <label htmlFor="garages_2">2+</label>
              </div>
            </div>
          </fieldset>
        </div>

        <fieldset className="form-group are-radios">
          <legend>¿Cuentas con cuarto útil?</legend>
          <div className="form-items">
            <div className="form-control is-radio">
              <input
                {...register("utility_room")}
                type="radio"
                id="utility_room_true"
                value="S"
                required
              />
              <label htmlFor="utility_room_true">Sí</label>
            </div>
            <div className="form-control is-radio">
              <input
                {...register("utility_room")}
                type="radio"
                id="utility_room_false"
                value="N"
                required
              />
              <label htmlFor="utility_room_false">No</label>
            </div>
          </div>
        </fieldset>

        <div className="flex flex-wrap gap-4">
          <div className="form-control grow">
            <label>Información adicional del inmueble</label>
            <textarea
              rows={8}
              id="description"
              {...register("description")}
              placeholder="Escribe aquí si deseas agregar algo importante de la distribución o características del inmueble"
              required
              maxLength={characterLimit}
            ></textarea>
            <div className="help-text text-co-dark-cyan">
              Has escrito {length} de {characterLimit} caracteres recomendados.
            </div>
          </div>
        </div>

        <div className="flex justify-between gap-2 md:flex-row">
          <Button
            type="button"
            onClick={onBack}
            className="co-button is-secondary is-large has-fixed-icon"
          >
            <i className="co-icon icon-arrow"></i>
            Regresar
          </Button>

          <Button
            loading={isSubmitting}
            className="co-button is-progress-default is-large w-full md:w-auto"
          >
            Continuar
          </Button>
        </div>
      </form>
    </div>
  );
}

export default Step3;
